import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useToast } from "@/hooks/use-toast";
import { useAppDispatch, useAppSelector } from "@/store";
import { AppRoute } from "@/types/routes";

import {
  clearReturnTo,
  selectReturnTo,
} from "../store/payment-method-form-slice";

/**
 * Ends a payment method change: the detour is over, so the return route and the
 * kind it required are dropped together and the participant is put back where
 * they were going. Shared by the select page, the creation flow and the two
 * card success pages, which all finish the same journey.
 */
export const useCompletePaymentMethodChange = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const returnTo = useAppSelector(selectReturnTo);

  const completePaymentMethodChange = useCallback(() => {
    dispatch(clearReturnTo());
    toast({
      description: t("paymentMethod_updated"),
    });
    navigate(returnTo ?? AppRoute.HOME);
  }, [returnTo, navigate, toast, t, dispatch]);

  /**
   * Finishes a live detour, or goes to `fallback` when there is none. The card
   * success pages end the same journey from two different places: on a detour
   * the participant is handed back to the claim they were starting, otherwise
   * they carry on to wherever that page normally leads.
   */
  const completeOrNavigate = useCallback(
    (fallback: AppRoute) => {
      if (returnTo) {
        completePaymentMethodChange();
        return;
      }
      toast({
        description: t("paymentMethod_updated"),
      });
      navigate(fallback);
    },
    [returnTo, completePaymentMethodChange, toast, t, navigate]
  );

  return { completePaymentMethodChange, completeOrNavigate, returnTo };
};
